import { FC, FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'antd';
import cs from 'classnames';

import LabelWithTooltip from 'components/LabelWithTooltip/LabelWithTooltip';

import { Job } from 'types/jobs';

import { SubscriptionTypes } from 'constants/enums';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import { ReactComponent as CloseCircle } from 'assets/icons/close-circle.svg';
import { ReactComponent as Danger } from 'assets/icons/danger-filled.svg';
import { ReactComponent as Slash } from 'assets/icons/slash.svg';
import { ReactComponent as TickCircle } from 'assets/icons/tick-circle-filled.svg';

import styles from './JobSubscriptionStatus.module.scss';
import {
  SubscriptionIcons,
  mapSubscriptionToStatusUI
} from './mapSubscriptionToStatusUI';

interface JobSubscriptionStatusProps {
  job: Job;
}

const iconMap: Record<
  SubscriptionIcons,
  { Icon: FunctionComponent<SVGProps<SVGSVGElement>>; styles: string }
> = {
  ok: { Icon: TickCircle, styles: cs(styles.icon, styles.ok) },
  warn: { Icon: Danger, styles: cs(styles.icon, styles.warn) },
  close: { Icon: CloseCircle, styles: cs(styles.icon, styles.closed) },
  slash: { Icon: Slash, styles: cs(styles.icon, styles.failed) }
};

const JobSubscriptionStatus: FC<JobSubscriptionStatusProps> = ({
  job
}: JobSubscriptionStatusProps) => {
  const { t } = useTranslation();

  if (!job) {
    return null;
  }

  const { subscriptionType, subscriptionStatus, subscriptionDueDate } = job;

  switch (subscriptionType) {
    case SubscriptionTypes.Free:
      return <LabelWithTooltip label={t('label.free')} />;
    case SubscriptionTypes.Concierge:
      return (
        <div className={styles.wrap}>
          <div className={styles.text}>{FALLBACK_VALUE}</div>
        </div>
      );
  }

  const { iconType, text, tooltip } = mapSubscriptionToStatusUI(
    t,
    subscriptionStatus,
    subscriptionDueDate
  );
  const icon = iconType ? iconMap[iconType] : null;

  return (
    <div className={styles.wrap}>
      <Tooltip title={tooltip} trigger={'hover'} className={styles.tooltip}>
        {icon && <icon.Icon className={icon.styles} />}
      </Tooltip>
      <div className={styles.text}>{text}</div>
    </div>
  );
};

export default JobSubscriptionStatus;
